import { Phone, Mail, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { useSite } from "@/site/SiteContext";
import { scrollToSection } from "@/lib/scroll";
import Logo from "@/components/Logo";

const NAV = [
  { id: "about", key: "nav.about" },
  { id: "services", key: "nav.services" },
  { id: "portfolio", key: "nav.portfolio" },
  { id: "contact", key: "nav.contact" },
];

const Footer = () => {
  const { t } = useLang();
  const { contact } = useSite();
  const scrollTo = (id) => scrollToSection(id);

  return (
    <footer className="bg-[#070707] border-t border-gold/15 px-6 md:px-12 pt-16 pb-8">
      <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-12">
        <div>
          <div className="flex items-center gap-3">
            <Logo size={48} className="shrink-0" />
            <div className="font-serif text-2xl text-foreground leading-none">
              <span className="gold-gradient-text">A&amp;K</span> Atelier
              <span className="block text-[9px] tracking-[0.25em] uppercase text-gold/70 mt-1.5">
                Artisan Créateur de Décors en Plâtre
              </span>
            </div>
          </div>
          <p className="mt-5 text-sm text-muted-foreground font-sans leading-relaxed max-w-xs">
            {t("footer.tagline")}
          </p>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-gold/80 font-sans">{t("footer.nav")}</div>
          <ul className="mt-5 space-y-3">
            {NAV.map((n) => (
              <li key={n.id}>
                <button
                  data-testid={`footer-nav-${n.id}`}
                  onClick={() => scrollTo(n.id)}
                  className="text-sm text-muted-foreground hover:text-gold transition-colors font-sans"
                >
                  {t(n.key)}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-gold/80 font-sans">{t("footer.contact")}</div>
          <ul className="mt-5 space-y-3">
            <li>
              <a href={`tel:${contact.phoneTel}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-gold transition-colors font-sans">
                <Phone size={15} /> {contact.phoneDisplay}
              </a>
            </li>
            <li>
              <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-gold transition-colors font-sans">
                <Mail size={15} /> {contact.email}
              </a>
            </li>
            <li>
              <a
                href={`https://wa.me/${contact.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-gold transition-colors font-sans"
              >
                <MessageCircle size={15} /> WhatsApp
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-14 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground font-sans">
          © {new Date().getFullYear()} A&amp;K Atelier. {t("footer.rights")}
        </span>
        <div className="flex items-center gap-5">
          <Link to="/privacy" data-testid="footer-privacy" className="text-xs text-muted-foreground hover:text-gold transition-colors font-sans">
            {t("footer.privacy")}
          </Link>
          <Link to="/mentions-legales" data-testid="footer-legal" className="text-xs text-muted-foreground hover:text-gold transition-colors font-sans">
            {t("footer.legal")}
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
